// Agile Software design-system primitives, recreated in the portal's own React
// from the Brand Kit v1.0 spec (tokens live in @agile/brand). App-local for now;
// promote to @agile/ui once a second app needs them.
import {
  type ReactNode,
  type CSSProperties,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type SelectHTMLAttributes,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";

export { Icon };

export type Tone =
  | "navy"
  | "teal"
  | "warning"
  | "danger"
  | "neutral"
  | "success";

/* ---------- Eyebrow ---------- */
export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className="ds-eyebrow">{children}</div>;
}

/* ---------- Badge ---------- */
export function Badge({
  tone = "neutral",
  dot = false,
  children,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`ds-badge ds-badge--${tone}`}>
      {dot ? <span className="ds-badge-dot" /> : null}
      {children}
    </span>
  );
}

/* ---------- Tag ---------- */
export function Tag({ children }: { children: ReactNode }) {
  return <span className="ds-tag">{children}</span>;
}

/* ---------- Card ---------- */
export function Card({
  accent = false,
  accentWidth = 48,
  interactive = false,
  padding = "24px",
  onClick,
  style,
  className,
  children,
}: {
  accent?: boolean;
  accentWidth?: number;
  interactive?: boolean;
  padding?: string;
  onClick?: () => void;
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
}) {
  const cls = [
    "ds-card",
    accent ? "ds-card--accent" : "",
    interactive ? "ds-card--interactive" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  const vars = {
    "--card-pad": padding,
    "--accent-w": `${accentWidth}px`,
    ...style,
  } as CSSProperties;
  if (interactive || onClick) {
    return (
      <button type="button" className={cls} style={vars} onClick={onClick}>
        {children}
      </button>
    );
  }
  return (
    <div className={cls} style={vars}>
      {children}
    </div>
  );
}

/* ---------- Button ---------- */
export function Button({
  variant = "primary",
  size = "md",
  icon,
  arrow = false,
  children,
  className,
  ...rest
}: {
  variant?: "primary" | "secondary" | "ghost" | "accent";
  size?: "sm" | "md";
  icon?: string;
  arrow?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const is = size === "sm" ? 14 : 16;
  return (
    <button
      type="button"
      className={`ds-btn ds-btn--${variant} ds-btn--${size} ${className ?? ""}`}
      {...rest}
    >
      {icon ? <Icon name={icon} size={is} /> : null}
      {children ? <span>{children}</span> : null}
      {arrow ? <Icon name="arrow-right" size={is} /> : null}
    </button>
  );
}

/* ---------- IconButton ---------- */
export function IconButton({
  icon,
  label,
  variant = "secondary",
  ...rest
}: {
  icon: string;
  label: string;
  variant?: "primary" | "secondary" | "ghost";
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`ds-iconbtn ds-iconbtn--${variant}`}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon name={icon} size={16} />
    </button>
  );
}

/* ---------- Input (text / textarea) ---------- */
type InputProps = {
  icon?: string;
  size?: "sm" | "md";
  multiline?: boolean;
  rows?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "size"> &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "size">;

export function Input({
  icon,
  size = "md",
  multiline = false,
  rows = 3,
  className,
  ...rest
}: InputProps) {
  if (multiline) {
    return (
      <textarea
        className={`ds-input ds-input--area ${className ?? ""}`}
        rows={rows}
        {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
    );
  }
  const field = (
    <input
      className={`ds-input ds-input--${size} ${icon ? "ds-input--icon" : ""} ${className ?? ""}`}
      {...(rest as InputHTMLAttributes<HTMLInputElement>)}
    />
  );
  if (!icon) return field;
  return (
    <span className="ds-input-wrap">
      <Icon name={icon} size={size === "sm" ? 14 : 16} />
      {field}
    </span>
  );
}

/* ---------- Select ---------- */
export function Select({
  options,
  size = "md",
  placeholder,
  value,
  onChange,
  ...rest
}: {
  options: string[];
  size?: "sm" | "md";
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "size">) {
  return (
    <span className={`ds-select-wrap ds-select-wrap--${size}`}>
      <select
        className={`ds-select ds-select--${size}`}
        value={value}
        onChange={onChange}
        {...(rest as object)}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <Icon name="chevron-down" size={size === "sm" ? 14 : 16} />
    </span>
  );
}

/* ---------- Field ---------- */
export function Field({
  label,
  required = false,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="ds-field">
      <span className="ds-field-label">
        {label}
        {required ? <span className="ds-field-req"> *</span> : null}
      </span>
      {children}
      {hint ? <span className="ds-field-hint">{hint}</span> : null}
    </label>
  );
}

/* ---------- Switch ---------- */
export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`ds-switch ${checked ? "is-on" : ""}`}
      onClick={() => onChange(!checked)}
    >
      <span className="ds-switch-knob" />
    </button>
  );
}

/* ---------- Tabs ---------- */
export interface TabItem {
  value: string;
  label: string;
  count?: number;
}
export function Tabs({
  items,
  value,
  onChange,
}: {
  items: TabItem[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="ds-tabs" role="tablist">
      {items.map((it) => (
        <button
          key={it.value}
          role="tab"
          aria-selected={value === it.value}
          className={`ds-tab ${value === it.value ? "is-on" : ""}`}
          onClick={() => onChange(it.value)}
        >
          {it.label}
          {typeof it.count === "number" ? (
            <span className="ds-tab-count">{it.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/* ---------- Dialog ---------- */
export function Dialog({
  open,
  title,
  description,
  accent = false,
  width = 560,
  onClose,
  footer,
  children,
}: {
  open: boolean;
  title: string;
  description?: string;
  accent?: boolean;
  width?: number;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <>
      <div className="ds-scrim" onClick={onClose} />
      <div
        className={`ds-dialog ${accent ? "ds-dialog--accent" : ""}`}
        style={{ width: `min(${width}px, calc(100vw - 40px))` }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="ds-dialog-head">
          <div>
            <h2 className="ds-dialog-title">{title}</h2>
            {description ? (
              <p className="ds-dialog-desc">{description}</p>
            ) : null}
          </div>
          <IconButton
            icon="x"
            label="Close"
            variant="ghost"
            onClick={onClose}
          />
        </div>
        <div className="ds-dialog-body">{children}</div>
        {footer ? <div className="ds-dialog-foot">{footer}</div> : null}
      </div>
    </>,
    document.body,
  );
}

/* ---------- Toast ---------- */
export function Toast({
  title,
  tone = "success",
  onClose,
}: {
  title: string;
  tone?: Tone;
  onClose: () => void;
}) {
  return (
    <div className={`ds-toast ds-toast--${tone}`} role="status">
      <Icon
        name={tone === "danger" ? "alert-triangle" : "circle-check"}
        size={16}
      />
      <span className="ds-toast-title">{title}</span>
      <button
        type="button"
        className="ds-toast-close"
        aria-label="Dismiss"
        onClick={onClose}
      >
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}

/* ---------- Logo ---------- */
export function Logo({
  variant = "reversed",
  width = 158,
}: {
  variant?: "reversed" | "primary";
  width?: number;
}) {
  const reversed = variant === "reversed";
  const mark = Math.round(width * 0.2);
  return (
    <span className="ds-logo" style={{ ["--logo-w" as string]: `${width}px` }}>
      <svg
        className="ds-logo-mark"
        width={mark}
        height={mark}
        viewBox="0 0 40 40"
        fill="none"
        aria-hidden
      >
        <defs>
          <linearGradient id="agile-a" x1="0" y1="0" x2="40" y2="40">
            <stop offset="0" stopColor="#2DD4BF" />
            <stop offset="1" stopColor="#0E7490" />
          </linearGradient>
        </defs>
        <path d="M20 3 L37 37 H27 L20 21 L13 37 H3 Z" fill="url(#agile-a)" />
      </svg>
      <span className="ds-logo-word">
        <span
          className="ds-logo-name"
          style={{ color: reversed ? "#fff" : "var(--ink-navy)" }}
        >
          AGILE
        </span>
        <span className="ds-logo-sub">SOFTWARE</span>
      </span>
    </span>
  );
}
